import React, { useState, useEffect } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import authService from '../../services/authService';
import dashboardService from '../../services/dashboardService';
import { ConfirmDialog } from '../../components/dialogs';
import { obfuscateEmail } from '../../utils/emailObfuscation';

const VigilanceDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [userComplaints, setUserComplaints] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  // Check if we're on the index route (dashboard home)
  const isDashboardHome = location.pathname === '/vigilance-dashboard' || location.pathname === '/vigilance-dashboard/';

  // Get user data from AuthContext
  const userData = user;

  // Load dashboard analytics when on dashboard home
  useEffect(() => {
    if (isDashboardHome) {
      const fetchDashboardData = async () => {
        setLoading(true);
        try {
          const response = await dashboardService.getUserDashboardAnalytics();
          if (response.success) {
            // Extract data from response - handle both direct data and nested data.data
            const analyticsData = response.data?.data || response.data;
            setDashboardData(analyticsData);
            
            // Extract recent complaints from the analytics data
            if (analyticsData?.recent_complaints) {
              setUserComplaints(Array.isArray(analyticsData.recent_complaints) ? analyticsData.recent_complaints : []);
            } else {
              setUserComplaints([]);
            }
          } else {
            console.error('Failed to fetch dashboard analytics:', response.message);
            setUserComplaints([]);
          }
        } catch (error) {
          console.error('Error fetching dashboard analytics:', error);
          setUserComplaints([]);
        } finally {
          setLoading(false);
        }
      };

      fetchDashboardData();
    }
  }, [isDashboardHome]);

  const handleLogoutClick = () => {
    setShowLogoutDialog(true);
  };

  const handleLogoutConfirm = async () => {
    setShowLogoutDialog(false);
    try {
      // Call logout API for user
      await authService.logout('user');
      
      // Clear auth context
      await logout();
      
      // Redirect to login page
      navigate('/vigilance-login');
    } catch (error) {
      console.error('Logout error:', error);
      // Even if API call fails, clear local data and redirect
      await logout();
      navigate('/vigilance-login');
    }
  };

  const handleLogoutCancel = () => {
    setShowLogoutDialog(false);
  };

  const handleCreateComplaint = () => {
    navigate('/vigilance-dashboard/create');
  };

  const handleViewAllComplaints = () => {
    navigate('/vigilance-dashboard/complaints');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left Side - SJVN Branding */}
            <div className="flex items-center">
              <img 
                src={`${process.env.PUBLIC_URL}/logo192.png`}
                alt="SJVN Limited Logo" 
                className="w-10 h-12 mr-3" 
                role="img" 
                aria-label="SJVN Limited Logo"
              />
              <div>
                <h2 className="text-lg font-bold text-gray-900">SJVN Limited</h2>
                <p className="text-xs text-gray-500">Vigilance Portal</p>
              </div>
            </div>
            
            {/* Center - Navigation */}
            <nav className="flex space-x-8">
              <button
                onClick={() => navigate('/vigilance-dashboard')}
                className={`px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                  isDashboardHome 
                    ? 'text-primary-600 border-b-2 border-primary-600' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Dashboard
              </button>
              <button
                onClick={handleViewAllComplaints}
                className={`px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                  location.pathname === '/vigilance-dashboard/complaints'
                    ? 'text-primary-600 border-b-2 border-primary-600' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                All Complaints
              </button>
              <button
                onClick={handleCreateComplaint}
                className={`px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                  location.pathname === '/vigilance-dashboard/create'
                    ? 'text-primary-600 border-b-2 border-primary-600' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Create New Vigilance
              </button>
            </nav>
            
            {/* Right Side - User Info and Logout */}
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">
                  {userData?.name || 'User'}
                </p>
                <p className="text-xs text-gray-500">
                  {userData?.email ? obfuscateEmail(userData.email) : userData?.phone}
                </p>
              </div>
              <button
                onClick={handleLogoutClick}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors duration-300"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Show Dashboard Content only on index route */}
        {isDashboardHome && (
          <>
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="flex flex-col items-center">
                  <svg className="animate-spin h-8 w-8 text-primary-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <p className="text-gray-600">Loading dashboard...</p>
                </div>
              </div>
            ) : (
              <>
                {/* Dashboard Overview */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                  {/* Total Complaints Card */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-500">Total Complaints</p>
                        <p className="text-2xl font-semibold text-gray-900">
                          {dashboardData?.total || 0}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Under Review Card */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                          <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-500">Under Review</p>
                        <p className="text-2xl font-semibold text-gray-900">
                          {dashboardData?.under_review || 0}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Resolved Card */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-500">Resolved</p>
                        <p className="text-2xl font-semibold text-gray-900">
                          {dashboardData?.resolved || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={handleCreateComplaint}
                  className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors duration-300"
                >
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4 text-left">
                    <p className="text-sm font-medium text-gray-900">Create New Complaint</p>
                    <p className="text-sm text-gray-500">Submit a new vigilance complaint</p>
                  </div>
                </button>

                <button
                  onClick={handleViewAllComplaints}
                  className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors duration-300"
                >
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4 text-left">
                    <p className="text-sm font-medium text-gray-900">View All Complaints</p>
                    <p className="text-sm text-gray-500">Track your submitted complaints</p>
                  </div>
                </button>
              </div>
            </div>

            {!loading && (
              <>
            {/* Recent Complaints */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Recent Complaints</h3>
                    <p className="text-sm text-gray-600 mt-1">Your latest submitted complaints</p>
                  </div>
                  <button
                    onClick={() => navigate('/vigilance-dashboard/complaints')}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
                  >
                    View All Complaints
                  </button>
                </div>
              </div>
              
              {userComplaints.length === 0 ? (
                <div className="px-6 py-12 text-center text-gray-500">
                  <div className="flex flex-col items-center">
                    <svg className="w-12 h-12 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-lg font-medium text-gray-900 mb-2">No complaints submitted yet</p>
                    <p className="text-gray-500 mb-4">Start by creating your first vigilance complaint</p>
                    <button
                      onClick={handleCreateComplaint}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      Create New Complaint
                    </button>
                  </div>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {userComplaints.map((complaint, index) => {
                    const complaintId = complaint.complaint_id || complaint.complaint_no || complaint.complaint_number || `#${index + 1}`;
                    const status = complaint.complaint_status || complaint.status || 'Pending';
                    const submittedDate = complaint.created_at 
                      ? new Date(complaint.created_at).toLocaleDateString('en-IN', { 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        })
                      : complaint.submitted_date 
                        ? new Date(complaint.submitted_date).toLocaleDateString('en-IN', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })
                        : 'N/A';
                    const address = complaint.address || 'N/A';
                    
                    const getStatusColor = (statusValue) => {
                      const statusLower = (statusValue || '').toLowerCase();
                      if (statusLower.includes('investigation')) {
                        return 'bg-yellow-100 text-yellow-800';
                      } else if (statusLower.includes('resolved') || statusLower.includes('closed')) {
                        return 'bg-green-100 text-green-800';
                      } else if (statusLower.includes('pending')) {
                        return 'bg-blue-100 text-blue-800';
                      }
                      return 'bg-gray-100 text-gray-800';
                    };

                    return (
                      <div key={complaintId || index} className="px-6 py-4 hover:bg-gray-50 transition-colors duration-200">
                        <div className="flex items-center space-x-3">
                          <p className="text-sm font-medium text-gray-900">
                            {complaintId}
                          </p>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(status)}`}>
                            {status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{address}</p>
                        <p className="text-xs text-gray-400 mt-1">Submitted on {submittedDate}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
              </>
            )}
          </>
        )}

        {/* Render nested routes only when not on dashboard home */}
        {!isDashboardHome && <Outlet />}
      </div>

      {/* Logout Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showLogoutDialog}
        title="Confirm Logout"
        message="Are you sure you want to logout? You will need to login again to access your dashboard."
        onConfirm={handleLogoutConfirm}
        onCancel={handleLogoutCancel}
        confirmText="Logout"
        cancelText="Cancel"
      />
    </div>
  );
};

export default VigilanceDashboard;
